import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { RejectedValue, ErrorResponse, MainResponse } from "shared/interfaces/core-interfaces";
import { CreateInvoiceNumberSettingsRequest, UpdateInvoiceNumberSettingsRequest, TenantSettingResponse, CreateOvertimeAbsenceSettingsRequest, UpdateOvertimeAbsenceSettingsRequest, initialTenantSettingsState, InvoiceNumberSettingsResponse, OvertimeAbsenceSettingsResponse } from "./tenantSettings";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { Guid } from "guid-typescript";
import { GetTenantNotificationSettingsResponse, UpdateTenantNotificationSettingsRequest } from "./tenant-notification-settings";
import { API_BASE_URL as baseUri } from "config/env";

export const GetTenantSetting = createAsyncThunk<TenantSettingResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetTenantSetting',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/tenant/settings/get?tenantId=' + request, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TenantSettingResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCustomInvoiceNumberSettings = createAsyncThunk<InvoiceNumberSettingsResponse | null, Guid, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetCustomInvoiceNumberSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/get?tenantId=' + request, {
            method: "GET",
            headers: AuthHeader(),
        });

        if (response.status === 200) {
            const responseModel = await response.json() as TenantSettingResponse;
            return responseModel.customInvoiceNumberSettings ?? null;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error}));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetOvertimeAndAbsenceSettings  = createAsyncThunk<OvertimeAbsenceSettingsResponse | null, Guid, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetOvertimeAndAbsenceSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/get?tenantId=' + request, {
            method: "GET",
            headers: AuthHeader(),
        });

        if (response.status === 200) {
            const responseModel = await response.json() as TenantSettingResponse;
            return responseModel.overtimeAndAbsenceSettings ?? null;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error}));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateInvoiceSettings = createAsyncThunk<MainResponse, CreateInvoiceNumberSettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/CreateInvoiceSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/custom-invoice-number/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const result = await response.json() as MainResponse;
            dispatch(showNotification({ message: result.message ?? "Invoice settings created.", type: NotificationTypes.Success }));
            return result;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateInvoiceSettings = createAsyncThunk<MainResponse, UpdateInvoiceNumberSettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/UpdateInvoiceSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/custom-invoice-number/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const result = await response.json() as MainResponse;
            dispatch(showNotification({ message: result.message ?? "Invoice settings updated.", type: NotificationTypes.Success }));
            return result;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateOvertimeAndAbsenceSettings = createAsyncThunk<MainResponse, CreateOvertimeAbsenceSettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/CreateOvertimeAndAbsenceSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/overtime-absence-settings/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const result = await response.json() as MainResponse;
            dispatch(showNotification({ message: result.message ?? "Overtime & Absence settings created.", type: NotificationTypes.Success }));
            return result;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateOvertimeAndAbsenceSettings = createAsyncThunk<MainResponse, UpdateOvertimeAbsenceSettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/UpdateOvertimeAndAbsenceSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/settings/overtime-absence-settings/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const result = await response.json() as MainResponse;
            dispatch(showNotification({ message: result.message ?? "Overtime & Absence settings updated.", type: NotificationTypes.Success }));
            return result;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// TENANT NOTIFICATION SETTINGS (WP7)
// Returns every notification type with the tenant's switch for each - workflow types included, so the
// section can render them locked. A type nobody ever touched comes back enabled.
export const GetTenantNotificationSettings = createAsyncThunk<GetTenantNotificationSettingsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetTenantNotificationSettings',
    async (_, { rejectWithValue, dispatch }) => {
        // No tenant id in the query: these settings are always the caller's own tenant, taken from the token.
        const response = await fetch(baseUri + 'api/tenant/notification-settings/get', {
            method: "GET",
            headers: AuthHeader(),
        });

        if (response.status === 200) {
            const responseModel = await response.json() as GetTenantNotificationSettingsResponse;
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// The save merges - types left out of the request keep their current value - and returns the resulting
// state, so there is no need to re-read after saving.
export const UpdateTenantNotificationSettings = createAsyncThunk<GetTenantNotificationSettingsResponse, UpdateTenantNotificationSettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/UpdateTenantNotificationSettings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/tenant/notification-settings/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });

        if (response.status === 200) {
            const responseModel = await response.json() as GetTenantNotificationSettingsResponse;
            dispatch(showNotification({ message: "Notification settings updated.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const TenantSettingSlice = createSlice({
    name: 'TenantSettingSlice',
    initialState: initialTenantSettingsState,
    reducers: {
        resetTenantSetting: (state) => {
            state.customInvoiceNumberSettings = null;
            state.overtimeAndAbsenceSettings = null;
            state.tenantNotificationSettings = null;
        },
    },
    extraReducers(builder) {
        builder
        .addCase(GetTenantSetting.fulfilled, (state, action: PayloadAction<TenantSettingResponse>) => {
            state.customInvoiceNumberSettings = action.payload.customInvoiceNumberSettings ?? null;
            state.overtimeAndAbsenceSettings = action.payload.overtimeAndAbsenceSettings ?? null;
        })
        .addCase(GetCustomInvoiceNumberSettings.fulfilled, (state, action) => {
            state.customInvoiceNumberSettings = action.payload ?? null;
        })
        .addCase(GetOvertimeAndAbsenceSettings.fulfilled, (state, action) => {
            state.overtimeAndAbsenceSettings = action.payload ?? null;
        })
        .addCase(GetTenantNotificationSettings.fulfilled, (state, action: PayloadAction<GetTenantNotificationSettingsResponse>) => {
            state.tenantNotificationSettings = action.payload.settings ?? null;
        })
        .addCase(UpdateTenantNotificationSettings.fulfilled, (state, action: PayloadAction<GetTenantNotificationSettingsResponse>) => {
            state.tenantNotificationSettings = action.payload.settings ?? null;
        });
    }
    // no rejected? pending?
});

export const { resetTenantSetting } = TenantSettingSlice.actions;
export default TenantSettingSlice.reducer;